import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  PlusIcon,
  TrashIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";
import { sectionContentApi } from "../api/cmsApi";
import toast from "react-hot-toast";

interface SectionContentItem {
  _id: string;
  variant: string;
  section: string;
  type: string;
  title: { de: string; en: string };
  description?: { de: string; en: string };
  order: number;
  isActive: boolean;
}

const variants = [
  { id: "default", label: "Standard (Google/Direct)" },
  { id: "facebook", label: "Facebook" },
  { id: "instagram", label: "Instagram" },
  { id: "x", label: "X (Twitter)" },
  { id: "linkedin", label: "LinkedIn" },
  { id: "reddit", label: "Reddit" },
  { id: "tiktok", label: "TikTok" },
];

const sections = [
  { id: "hero", label: "Hero" },
  { id: "howItWorks", label: "How It Works" },
  { id: "features", label: "Features / Benefits" },
  { id: "cta", label: "CTA" },
  { id: "trustIndicators", label: "Trust Indicators" },
];

const contentTypes = [
  { id: "trustPoints", label: "Trust Points", section: "hero", hasDescription: false, hasIcon: false },
  { id: "steps", label: "Steps", section: "howItWorks", hasDescription: true, hasIcon: false },
  { id: "items", label: "Items", section: "features", hasDescription: true, hasIcon: false },
  { id: "points", label: "Bullet Points", section: "cta", hasDescription: false, hasIcon: false },
  { id: "items", label: "Trust Items", section: "trustIndicators", hasDescription: false, hasIcon: true },
];

export default function SectionContent() {
  const queryClient = useQueryClient();
  const [selectedVariant, setSelectedVariant] = useState("default");
  const [selectedSection, setSelectedSection] = useState("hero");
  const [selectedType, setSelectedType] = useState("trustPoints");
  const [isCreating, setIsCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const currentTypeConfig = contentTypes.find(t => t.id === selectedType);

  const [formData, setFormData] = useState({
    title: { de: "", en: "" },
    description: { de: "", en: "" },
    icon: "",
  });

  const { data: items = [], isLoading } = useQuery({
    queryKey: ["sectionContent", selectedVariant, selectedSection, selectedType],
    queryFn: () => sectionContentApi.list(selectedVariant, selectedSection, selectedType).then((res) => res.data.data || []),
  });

  const createMutation = useMutation({
    mutationFn: (data: any) => sectionContentApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sectionContent"] });
      setIsCreating(false);
      resetForm();
      toast.success("Inhalt erstellt");
    },
    onError: () => toast.error("Fehler beim Erstellen"),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => sectionContentApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sectionContent"] });
      setEditingId(null);
      resetForm();
      toast.success("Inhalt aktualisiert");
    },
    onError: () => toast.error("Fehler beim Aktualisieren"),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => sectionContentApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sectionContent"] });
      toast.success("Inhalt gelöscht");
    },
    onError: () => toast.error("Fehler beim Löschen"),
  });

  const reorderMutation = useMutation({
    mutationFn: (reorderedItems: { id: string; order: number }[]) => sectionContentApi.reorder(reorderedItems),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sectionContent"] });
      toast.success("Reihenfolge aktualisiert");
    },
    onError: () => toast.error("Fehler beim Aktualisieren der Reihenfolge"),
  });

  const resetForm = () => {
    setFormData({
      title: { de: "", en: "" },
      description: { de: "", en: "" },
      icon: "",
    });
  };

  const handleCreate = () => {
    const data: any = {
      variant: selectedVariant,
      section: selectedSection,
      type: selectedType,
      title: formData.title,
      order: items.length,
    };
    if (currentTypeConfig?.hasDescription) {
      data.description = formData.description;
    }
    if (currentTypeConfig?.hasIcon && formData.icon) {
      data.icon = formData.icon;
    }
    createMutation.mutate(data);
  };

  const handleUpdate = (id: string) => {
    const data: any = { title: formData.title };
    if (currentTypeConfig?.hasDescription) {
      data.description = formData.description;
    }
    if (currentTypeConfig?.hasIcon) {
      data.icon = formData.icon || undefined;
    }
    updateMutation.mutate({ id, data });
  };

  const handleToggleActive = (item: SectionContentItem) => {
    updateMutation.mutate({
      id: item._id,
      data: { isActive: !item.isActive },
    });
  };

  const startEditing = (item: SectionContentItem) => {
    setEditingId(item._id);
    setFormData({
      title: item.title || { de: "", en: "" },
      description: item.description || { de: "", en: "" },
      icon: (item as any).icon || "",
    });
  };

  const moveItem = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= items.length) return;

    const reorderedItems = items.map((item: SectionContentItem, i: number) => {
      if (i === index) return { id: item._id, order: newIndex };
      if (i === newIndex) return { id: item._id, order: index };
      return { id: item._id, order: i };
    });
    reorderMutation.mutate(reorderedItems);
  };

  // Filter available types based on selected section
  const availableTypes = contentTypes.filter(t => t.section === selectedSection);

  // Update selected type when section changes
  const handleSectionChange = (sectionId: string) => {
    setSelectedSection(sectionId);
    const firstType = contentTypes.find(t => t.section === sectionId);
    if (firstType) {
      setSelectedType(firstType.id);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div className="flex items-center space-x-3">
          <DocumentTextIcon className="h-8 w-8 text-gray-700 dark:text-gray-300" />
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Section Content</h1>
            <p className="text-gray-600 dark:text-gray-400">
              Verwalten Sie wiederholbare Inhalte wie Steps, Trust Points, und mehr
            </p>
          </div>
        </div>
        <button
          onClick={() => {
            setIsCreating(true);
            resetForm();
          }}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Neuer Inhalt
        </button>
      </div>

      {/* Filters */}
      <div className="space-y-3">
        {/* Variant Tabs */}
        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 uppercase tracking-wide">
            Kanal / Variante
          </label>
          <div className="flex gap-2 overflow-x-auto pb-2">
            {variants.map((variant) => (
              <button
                key={variant.id}
                onClick={() => setSelectedVariant(variant.id)}
                className={`
                  px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors
                  ${selectedVariant === variant.id
                    ? "bg-indigo-600 text-white dark:bg-indigo-500"
                    : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700"
                  }
                `}
              >
                {variant.label}
              </button>
            ))}
          </div>
        </div>

        {/* Section Tabs */}
        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 uppercase tracking-wide">
            Sektion
          </label>
          <div className="flex gap-2 overflow-x-auto pb-2">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => handleSectionChange(section.id)}
                className={`
                  px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors
                  ${selectedSection === section.id
                    ? "bg-emerald-600 text-white dark:bg-emerald-500"
                    : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700"
                  }
                `}
              >
                {section.label}
              </button>
            ))}
          </div>
        </div>

        {/* Type Tabs */}
        {availableTypes.length > 1 && (
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 uppercase tracking-wide">
              Typ
            </label>
            <div className="flex gap-2 overflow-x-auto pb-2">
              {availableTypes.map((type) => (
                <button
                  key={type.id}
                  onClick={() => setSelectedType(type.id)}
                  className={`
                    px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors
                    ${selectedType === type.id
                      ? "bg-purple-600 text-white dark:bg-purple-500"
                      : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700"
                    }
                  `}
                >
                  {type.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Create Form */}
      {isCreating && (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg mb-6">
          <div className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Neuen Inhalt erstellen ({currentTypeConfig?.label})
            </h3>
            <div className="space-y-4">
              {/* Title DE/EN */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Titel</label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <span className="text-xs text-gray-500 dark:text-gray-400">Deutsch</span>
                    <input
                      type="text"
                      value={formData.title.de}
                      onChange={(e) => setFormData({ ...formData, title: { ...formData.title, de: e.target.value } })}
                      className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      placeholder="Deutscher Titel"
                    />
                  </div>
                  <div>
                    <span className="text-xs text-gray-500 dark:text-gray-400">English</span>
                    <input
                      type="text"
                      value={formData.title.en}
                      onChange={(e) => setFormData({ ...formData, title: { ...formData.title, en: e.target.value } })}
                      className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      placeholder="English title"
                    />
                  </div>
                </div>
              </div>

              {/* Description DE/EN (only for types that need it) */}
              {currentTypeConfig?.hasDescription && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Beschreibung</label>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <span className="text-xs text-gray-500 dark:text-gray-400">Deutsch</span>
                      <textarea
                        value={formData.description.de}
                        onChange={(e) => setFormData({ ...formData, description: { ...formData.description, de: e.target.value } })}
                        className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                        rows={2}
                        placeholder="Deutsche Beschreibung"
                      />
                    </div>
                    <div>
                      <span className="text-xs text-gray-500 dark:text-gray-400">English</span>
                      <textarea
                        value={formData.description.en}
                        onChange={(e) => setFormData({ ...formData, description: { ...formData.description, en: e.target.value } })}
                        className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                        rows={2}
                        placeholder="English description"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Icon (for trust indicators) */}
              {currentTypeConfig?.hasIcon && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Icon (Lucide)</label>
                  <input
                    type="text"
                    value={formData.icon}
                    onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                    className="w-full max-w-xs rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    placeholder="z.B. shield, lock, award, badge-check"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Lucide Icon Name — z.B. shield, lock, building2, award, badge-check, scale, handshake, eye, flame
                  </p>
                </div>
              )}

              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => setIsCreating(false)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                >
                  Abbrechen
                </button>
                <button
                  onClick={handleCreate}
                  disabled={createMutation.isPending}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 disabled:opacity-50"
                >
                  {createMutation.isPending ? "Erstellen..." : "Erstellen"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Items List */}
      <div className="space-y-2">
        {isLoading ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">Laden...</div>
        ) : items.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-12 text-center">
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              Keine Inhalte für diese Auswahl vorhanden.
            </p>
            <button
              onClick={() => setIsCreating(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600"
            >
              Ersten Inhalt erstellen
            </button>
          </div>
        ) : (
          items.map((item: SectionContentItem, index: number) => (
            <div key={item._id} className="bg-white dark:bg-gray-800 shadow rounded-lg">
              {editingId === item._id ? (
                <div className="p-4">
                  <div className="space-y-4">
                    {/* Title DE/EN */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Titel</label>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <span className="text-xs text-gray-500 dark:text-gray-400">Deutsch</span>
                          <input
                            type="text"
                            value={formData.title.de}
                            onChange={(e) => setFormData({ ...formData, title: { ...formData.title, de: e.target.value } })}
                            className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                          />
                        </div>
                        <div>
                          <span className="text-xs text-gray-500 dark:text-gray-400">English</span>
                          <input
                            type="text"
                            value={formData.title.en}
                            onChange={(e) => setFormData({ ...formData, title: { ...formData.title, en: e.target.value } })}
                            className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Description DE/EN */}
                    {currentTypeConfig?.hasDescription && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Beschreibung</label>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <span className="text-xs text-gray-500 dark:text-gray-400">Deutsch</span>
                            <textarea
                              value={formData.description.de}
                              onChange={(e) => setFormData({ ...formData, description: { ...formData.description, de: e.target.value } })}
                              className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                              rows={2}
                            />
                          </div>
                          <div>
                            <span className="text-xs text-gray-500 dark:text-gray-400">English</span>
                            <textarea
                              value={formData.description.en}
                              onChange={(e) => setFormData({ ...formData, description: { ...formData.description, en: e.target.value } })}
                              className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                              rows={2}
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Icon (for trust indicators) */}
                    {currentTypeConfig?.hasIcon && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Icon (Lucide)</label>
                        <input
                          type="text"
                          value={formData.icon}
                          onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                          className="w-full max-w-xs rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                          placeholder="z.B. shield, lock, award"
                        />
                      </div>
                    )}

                    <div className="flex gap-2 justify-end">
                      <button
                        onClick={() => setEditingId(null)}
                        className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                      >
                        <XMarkIcon className="h-4 w-4 mr-1" />
                        Abbrechen
                      </button>
                      <button
                        onClick={() => handleUpdate(item._id)}
                        disabled={updateMutation.isPending}
                        className="inline-flex items-center px-3 py-1.5 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 disabled:opacity-50"
                      >
                        <CheckIcon className="h-4 w-4 mr-1" />
                        Speichern
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-4 flex items-center gap-4">
                  {/* Reorder buttons */}
                  <div className="flex flex-col gap-1">
                    <button
                      onClick={() => moveItem(index, 'up')}
                      disabled={index === 0}
                      className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-30"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                      </svg>
                    </button>
                    <button
                      onClick={() => moveItem(index, 'down')}
                      disabled={index === items.length - 1}
                      className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-30"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  </div>

                  {/* Order number */}
                  <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-sm font-medium text-gray-600 dark:text-gray-400">
                    {index + 1}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    {currentTypeConfig?.hasIcon && (item as any).icon && (
                      <span className="inline-block px-2 py-0.5 mb-1 text-xs font-mono bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded">
                        {(item as any).icon}
                      </span>
                    )}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <span className="text-xs text-gray-400 uppercase">DE</span>
                        <p className="font-medium text-gray-900 dark:text-white truncate">
                          {item.title?.de || '-'}
                        </p>
                        {currentTypeConfig?.hasDescription && item.description?.de && (
                          <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                            {item.description.de}
                          </p>
                        )}
                      </div>
                      <div>
                        <span className="text-xs text-gray-400 uppercase">EN</span>
                        <p className="font-medium text-gray-900 dark:text-white truncate">
                          {item.title?.en || '-'}
                        </p>
                        {currentTypeConfig?.hasDescription && item.description?.en && (
                          <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                            {item.description.en}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleToggleActive(item)}
                      className={`
                        px-3 py-1 rounded-full text-xs font-medium
                        ${item.isActive
                          ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"
                          : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400"
                        }
                      `}
                    >
                      {item.isActive ? "Aktiv" : "Inaktiv"}
                    </button>
                    <button
                      onClick={() => startEditing(item)}
                      className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => {
                        if (confirm("Inhalt wirklich löschen?")) {
                          deleteMutation.mutate(item._id);
                        }
                      }}
                      className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
